<template>
  <div class="vertical-gallery">

    <!-- Tab navigation -->
    <div class="row mb-2 mt-3" v-if="shouldShowTabs">
      <div class="w-auto mx-auto ">
        <b-nav pills align="center" class="tab-gallery-navigation">
          <b-nav-item v-for="tab in availableTabs"
                      :key="tab"
                      :active="activeTab === tab"
                      :disabled="disabledTabs.includes(tab)"
                      @click="changeTab(tab)">
            {{ getTabLabel(tab) }}
          </b-nav-item>
        </b-nav>
      </div>
    </div>
    <GalleryBar :title="gallery.name"
                :hasPriceList="hasPriceList"
                @show-price-list="showPriceList"
                v-if="gallery.name || hasPriceList"/>

    <PackageCustom :data="packageCustom"
               :currency="currency"
               :in-cart="packageCustomInCart"
               class="mb-4"
               @activate-custom-package="activatePackageCustom"
               v-if="showPackageCustom && packageCustomPosition === ctaPosition.TOP"/>

    <!-- Tab content -->
    <component
        :is="currentTabComponent"
        :gallery="gallery"
        :media="mediaList"
        :active-tab="activeTab"
        :loading="loading"
        :loading-more="loadingMore"
        :current-page="currentPage"
        :total-pages="totalPages"
        :pagination-size="getPaginationSize()"
        :pagination-type="paginationType"
        :total-media="activeTabCount"
        :digital-item-id="digitalItemId"
        :favorite-enabled="favoriteEnabled"
        @open-media="openMedia($event)"
        @add-to-favourites="handleAddToFavourites"
        @remove-from-favourites="handleRemoveFromFavourites"
        @purchase-media="handlePurchaseMedia"
        @change-page="changePage"
        @load-more="loadMore"
    />

    <PackageButtons :package-price-list="packagePriceList"
                    @activate-package="activatePackage"
                    v-if="showPriceListCta"/>

    <PackageCustom :data="packageCustom"
                   :currency="currency"
                   :in-cart="packageCustomInCart"
                   class="mt-5"
                   @activate-custom-package="activatePackageCustom"
                   v-if="showPackageCustom && packageCustomPosition === ctaPosition.BOTTOM"/>

    <ModalMediaItem :medias="mediaList"
                    :total-medias="activeTabCount"
                    :page="mediaList.length"
                    :pagination-mode="paginationType"
                    :is-paid="activeTab === GALLERY_TAB_TYPE.UNPURCHASED"
                    :gallery-type="gallery.type"
                    :digital-item-id="digitalItemId"
                    :ref="modalMediasGalleryId"
                    :modal-id="modalMediasGalleryId"
                    :gallery="gallery"
                    :favorite-enabled="favoriteEnabled"
                    @add-to-favourites="handleAddToFavourites"
                    @remove-from-favourites="handleRemoveFromFavourites"
                    @load-more="loadMore"/>

     <ModalPriceList :modal-id="modalPriceListId"
                :event-name="eventName"
                :currency="currency"
                :price-list="galleryPriceList"
                :price-list-img="priceListImg"
                v-if="hasPriceList"/>
  </div>
</template>

<script>
import BaseDisplayType from './BaseDisplayType.vue'
import {GALLERY_TAB_TYPE, GALLERY_TAB_TYPE_LABELS} from '../constants/galleryTabType'
import VerticalMedia from './vertical/VerticalMedia.vue'
import VerticalFavourite from './vertical/VerticalFavourite.vue'
import VerticalAllMedia from './vertical/VerticalAllMedia.vue'
import VerticalPurchased from './vertical/VerticalPurchased.vue'
import VerticalUnpurchased from './vertical/VerticalUnpurchased.vue'
import HeaderBar from "@/domain/galleries/components/HeaderBar.vue";
import GalleryBar from "@/domain/galleryTree/components/GalleryBar.vue";
import ModalMedia from "@/domain/galleries/modals/ModaMedia.vue";
import ModalPriceList from "@/domain/galleries/modals/ModalPriceList.vue";
import {mapActions, mapGetters} from "vuex";
import PackageButtons from "@/domain/galleries/components/PackageButtons.vue";
import {orderTypes} from "@/domain/cart/constants";
import PackageCustom from "@/domain/galleries/components/PackageCustom.vue";
import ModalMediaItem from "@/domain/galleryTree/components/ModaMediaItem.vue";

export default {
  name: 'VerticalGallery',
  extends: BaseDisplayType,

  components: {
    ModalMediaItem,
    PackageCustom,
    PackageButtons,
    ModalPriceList,
    ModalMedia,
    GalleryBar,
    HeaderBar,
    VerticalMedia,
    VerticalFavourite,
    VerticalAllMedia,
    VerticalPurchased,
    VerticalUnpurchased
  },

  computed: {
    ...mapGetters('checkinDetail', ['checkin', 'picaCode', 'eventName', 'currency']),

    GALLERY_TAB_TYPE() {
      return GALLERY_TAB_TYPE
    },
    displayType() {
      return 'vertical'
    },

    hasTabSupport() {
      return true // Vertical supports tabs
    },

    paginationType() {
      // Use pagination if more than 4 pages, otherwise infinite scroll
      return this.totalPages > 4 ? 'pagination' : 'infinite'
    },

    currentTabComponent() {
      switch (this.activeTab) {
        case GALLERY_TAB_TYPE.MEDIA:
          return 'VerticalMedia'
        case GALLERY_TAB_TYPE.FAVOURITE:
          return 'VerticalFavourite'
        case GALLERY_TAB_TYPE.ALL_MEDIA:
          return 'VerticalAllMedia'
        case GALLERY_TAB_TYPE.PURCHASED:
          return 'VerticalPurchased'
        case GALLERY_TAB_TYPE.UNPURCHASED:
          return 'VerticalUnpurchased'
        default:
          return 'VerticalMedia'
      }
    },
    numMediaInCart() {
      return this.digitalItem && this.digitalItem.media_ids ? this.digitalItem.media_ids.length : 0
    }
  },

  methods: {
    ...mapActions('cartDigitalItems', ['createDigitalItem', 'setDigitalItemType', "addAllMediasToDigitalItem"]),
    getPaginationSize() {
      return 25 // Vertical view shows 20 items per page
    },

    getTabLabel(tab) {
      return GALLERY_TAB_TYPE_LABELS[tab] ? this.$t(GALLERY_TAB_TYPE_LABELS[tab]) : tab
    },

    handlePurchaseMedia(media) {
      // Emit event to handle media purchase
      this.$emit('purchase-media', media)
    },

    async activatePackage(orderType = orderTypes.PHOTOPACKAGE) {
      try {
        if (this.digitalItemId) {
          await this.setDigitalItemType({digitalItemId: this.digitalItemId, params: {order_type: orderType}})
        } else {
          await this.createDigitalItem({
            order_type: orderType,
            media_ids: [],
            checkin_id: this.checkin.id,
            gallery_id: this.gallery.id
          })
        }
        await this.$router.push({name: 'checkout'})
      } catch (error) {
        const responseError = error.response
        if (responseError.status === 400) {
          if (responseError.data.code === 'cart_currency_mismatch') {
            this.$bvModal.show('modal-currency-mismatch')
          } else if (responseError.data.code === 'purchase_disabled') {
            this.$bvToast.toast(responseError.data.detail, {
              title: this.$t('collage_photo_item.purchase_not_available'),
              headerClass: 'd-block pt-2',
              autoHideDelay: 5000,
              toaster: 'b-toaster-top-center'
            });
          }
        }
        throw error
      }
    },

    async activatePackageCustom() {
      try {
        await this.addAllMediasToDigitalItem({
          checkin_id: this.checkin.id,
          gallery_id: this.gallery.id
        })
        await this.$router.push({name: 'checkout'})
      } catch (error) {
        this.$bvToast.toast(this.$t('collage_photo_item.purchase_not_available'), {
          headerClass: 'd-block pt-2',
          variant: 'danger',
          autoHideDelay: 5000,
          toaster: 'b-toaster-top-center'
        });
      }
    },
  }
}
</script>
